/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/BuildPoliciesPanel" />
import React = require("react");
import { BuildDefinitionSummary, IBuildPolicy, IPolicy } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableValue, IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown, DropdownCallout } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { getSafeId } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { renderTooltipWithBreaks } from "./Util";

interface IBuildPoliciesPanelProps {
    buildDefinitions: IReadonlyObservableArray<IListBoxItem<BuildDefinitionSummary>>;
    buildDefinitionsLoading: ObservableValue<boolean>;
    onDismiss: () => void;
    policy: IBuildPolicy;
    save: (policy: IPolicy, handleError: (error: any) => void) => void;
}

const requiredId = "required-build";
const optionalId = "optional-build";

export class BuildPoliciesPanel extends VssComponent<IBuildPoliciesPanelProps> {
    private buildExpirationValue: IObservableValue<string>;
    private disabled: boolean;
    private displayNameValue: IObservableValue<string>;
    private errorMessageAfterXHours: IObservableValue<string | undefined>;
    private failureMessage: IObservableValue<string | undefined>;
    private isBusy: IObservableValue<boolean>;
    private isToggled: IObservableValue<boolean>;
    private showNoBuildDefsMessage: IObservableValue<boolean>;
    private pathFilterValue: IObservableValue<string>;
    private policy: IBuildPolicy;
    private selection: IListSelection;
    private selectedExpirationType: IObservableValue<string>;
    private selectedPolicyRequirement: IObservableValue<string>;
    private selectedTrigger: IObservableValue<string>;
    private title: string;
    private subscribedToBuildDefinitions: boolean;

    public constructor(props: IBuildPoliciesPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.policy = { ...this.props.policy };
        this.policy.complexSettings = { ...this.policy.complexSettings! };

        this.showNoBuildDefsMessage = new ObservableValue<boolean>(false);
        this.selection = new DropdownSelection();
        this.isBusy = new ObservableValue<boolean>(false);

        if (this.policy.isEnterpriseManaged || this.policy.isInherited || this.policy.disabled) {
            this.title = Resources.BuildPolicyView;
            this.disabled = true;
        } else {
            this.disabled = false;

            if (this.policy.id === -1) {
                this.title = Resources.BuildPolicyAdd;
            } else {
                this.title = Resources.BuildPolicyEdit;
            }
        }

        this.failureMessage = new ObservableValue<string | undefined>(undefined);
        this.errorMessageAfterXHours = new ObservableValue<string | undefined>(undefined);
        this.pathFilterValue = new ObservableValue<string>(this.policy.complexSettings.filenamePatterns || "");
        this.buildExpirationValue = new ObservableValue<string>(this.policy.complexSettings.validDuration || "12");
        this.displayNameValue = new ObservableValue<string>(this.policy.complexSettings.displayName || "");
        this.selectedExpirationType = new ObservableValue<string>(
            !this.policy.complexSettings.queueOnSourceUpdateOnly
                ? "immediately"
                : this.policy.complexSettings.validDuration === "0"
                ? "never"
                : "afterXTime"
        );
        this.selectedTrigger = new ObservableValue<string>(this.policy.complexSettings.manualQueueOnly ? "manual" : "automatic");
        this.selectedPolicyRequirement = new ObservableValue<string>(this.policy.complexSettings.required ? requiredId : optionalId);

        if (this.selectedExpirationType.value != "afterXTime") {
            this.buildExpirationValue.value = "";
        } else if (this.buildExpirationValue.value === "" || this.buildExpirationValue.value === "0") {
            this.buildExpirationValue.value = "12";
        }

        this.isToggled = new ObservableValue<boolean>(this.policy.value);
    }

    public componentDidMount = () => {
        if (this.props.buildDefinitionsLoading.value) {
            this.props.buildDefinitions.subscribe(this.selectInitialDropdownValue);
            this.subscribedToBuildDefinitions = true;
        } else {
            this.subscribedToBuildDefinitions = false;
        }

        if (this.props.buildDefinitions.length > 0) {
            this.selectInitialDropdownValue();
        }
    };

    public componentWillUnmount = () => {
        if (this.subscribedToBuildDefinitions) {
            this.props.buildDefinitions.unsubscribe(this.selectInitialDropdownValue);
        }
    };

    public render = (): JSX.Element => {
        const content = (
            <div className="flex-column rhythm-vertical-16 flex-grow">
                <Observer failureMessage={this.failureMessage}>
                    {() => {
                        return this.failureMessage.value ? (
                            <MessageCard
                                onDismiss={() => (this.failureMessage.value = undefined)}
                                severity={MessageCardSeverity.Error}
                                className="flex-noshrink"
                            >
                                <div className="flex-column font-size-ms">{this.failureMessage.value}</div>
                            </MessageCard>
                        ) : null;
                    }}
                </Observer>
                {this.policy.id !== -1 && (
                    <div>
                        <FormItem label={Resources.Enabled}>
                            <Toggle
                                offText={Resources.ToggleOff}
                                onText={Resources.ToggleOn}
                                checked={this.isToggled}
                                onChange={(event, value) => {
                                    this.isToggled.value = value;
                                    this.policy.value = value;
                                }}
                                disabled={this.disabled}
                            />
                        </FormItem>
                    </div>
                )}
                <div>
                    <FormItem label={Resources.BuildPipeline}>
                        <Dropdown
                            className="build-policy-dropdown"
                            items={this.props.buildDefinitions}
                            loading={this.props.buildDefinitionsLoading}
                            onSelect={(event, listBoxItem) => {
                                this.policy.complexSettings!.buildDefinitionId = Number(listBoxItem.id);
                            }}
                            selection={this.selection}
                            disabled={this.disabled}
                            renderCallout={props => (
                                <DropdownCallout
                                    {...props}
                                    onFilterTextChanged={(event, text) => {
                                        const lastItem = this.props.buildDefinitions.value[this.props.buildDefinitions.length - 1];
                                        if (lastItem.id === "loading-row") {
                                            // Set search text in the loading row, so it never gets hidden by filtering
                                            lastItem.text = text;
                                        }

                                        props.onFilterTextChanged?.(event, text);
                                    }}
                                />
                            )}
                        />
                    </FormItem>
                </div>
                <FormItem label={Resources.PathFilterOptional}>
                    <div className="flex-row flex-center flex-grow">
                        <TextField
                            className="icon-margin flex-grow"
                            value={this.pathFilterValue}
                            onChange={(event, value) => {
                                this.pathFilterValue.value = value;
                                this.policy.complexSettings!.filenamePatterns = value;
                            }}
                            width={TextFieldWidth.auto}
                            disabled={this.disabled}
                            containerClassName="flex-grow"
                            inputClassName="flex-grow"
                        />
                        <Icon
                            ariaLabel={Resources.PathFilterInfo}
                            iconName="Info"
                            tooltipProps={{
                                renderContent: () => renderTooltipWithBreaks(Resources.PathFilterInfo)
                            }}
                        />
                    </div>
                </FormItem>
                <div>
                    <FormItem label={Resources.Trigger}>
                        <RadioButtonGroup
                            onSelect={id => {
                                this.selectedTrigger.value = id;
                                this.policy.complexSettings!.manualQueueOnly = id == "automatic" ? false : true;
                            }}
                            selectedButtonId={this.selectedTrigger}
                            className="flex-start policy-build-radio-button-group"
                        >
                            <RadioButton id={"automatic"} ariaLabelledBy={"automatic-rblabel"} key={"automatic"} disabled={this.disabled}>
                                <div className="bolt-radio-button-label" id={getSafeId("automatic-rblabel")}>
                                    {Resources.TriggerAutomaticBuildPolicy}
                                </div>
                            </RadioButton>
                            <RadioButton
                                id={"manual"}
                                ariaLabelledBy={"manual-rblabel"}
                                key={"manual"}
                                className="policy-build-radio-button margin-top-16"
                                disabled={this.disabled}
                            >
                                <div className="bolt-radio-button-label" id={getSafeId("manual-rblabel")}>
                                    {Resources.TriggerManual}
                                </div>
                            </RadioButton>
                        </RadioButtonGroup>
                    </FormItem>
                </div>
                <div>
                    <FormItem label={Resources.PolicyRequirement}>
                        <RadioButtonGroup
                            onSelect={id => {
                                this.selectedPolicyRequirement.value = id;
                                this.policy.complexSettings!.required = id == requiredId ? true : false;
                            }}
                            selectedButtonId={this.selectedPolicyRequirement}
                            className="flex-start policy-build-radio-button-group"
                        >
                            <RadioButton id={requiredId} ariaLabelledBy={requiredId + "-rblabel"} key={requiredId} disabled={this.disabled}>
                                <div
                                    className="policy-build-radio-button-custom-label bolt-radio-button-label"
                                    id={getSafeId(requiredId + "-rblabel")}
                                >
                                    {Resources.Required}
                                    <span className="body-s secondary-text">{Resources.PolicyRequirementRequiredDesc}</span>
                                </div>
                            </RadioButton>
                            <RadioButton
                                id={optionalId}
                                ariaLabelledBy={optionalId + "-rblabel"}
                                key={optionalId}
                                className="margin-top-16"
                                disabled={this.disabled}
                            >
                                <div
                                    className="policy-build-radio-button-custom-label bolt-radio-button-label"
                                    id={getSafeId(optionalId + "-rblabel")}
                                >
                                    {Resources.Optional}
                                    <span className="body-s secondary-text">{Resources.PolicyRequirementOptionalDesc}</span>
                                </div>
                            </RadioButton>
                        </RadioButtonGroup>
                    </FormItem>
                </div>
                <div>
                    <FormItem
                        label={Resources.BuildExpiration}
                        error={!!this.errorMessageAfterXHours.value}
                        message={this.errorMessageAfterXHours.value}
                    >
                        <RadioButtonGroup
                            onSelect={id => {
                                this.selectedExpirationType.value = id;
                                this.errorMessageAfterXHours.value = undefined;
                                if (id === "immediately") {
                                    this.policy.complexSettings!.queueOnSourceUpdateOnly = false;
                                    this.buildExpirationValue.value = "";
                                } else if (id === "afterXTime") {
                                    this.policy.complexSettings!.queueOnSourceUpdateOnly = true;
                                    this.buildExpirationValue.value = "12";
                                    this.policy.complexSettings!.validDuration = "12";
                                } else {
                                    this.policy.complexSettings!.queueOnSourceUpdateOnly = true;
                                    this.buildExpirationValue.value = "";
                                }

                                this.policy.complexSettings!.validDuration = this.buildExpirationValue.value || "0";
                            }}
                            selectedButtonId={this.selectedExpirationType}
                            className="flex-start policy-build-radio-button-group"
                        >
                            <RadioButton
                                id={"immediately"}
                                ariaLabelledBy={"immediately-rblabel"}
                                key={"immediately"}
                                className="policy-build-radio-button"
                                disabled={this.disabled}
                            >
                                <div className="bolt-radio-button-label" id={getSafeId("immediately-rblabel")}>
                                    <span>
                                        {Resources.BuildExpirationImmediately1}
                                        <span>
                                            <Icon ariaHidden={"true"} className={"build-policies-afterXTime-icon"} iconName={"OpenSource"} />
                                            <span>{this.policy.confgiuredOn || this.policy.appliesTo}</span>
                                        </span>
                                        {Resources.BuildExpirationImmediately2}
                                    </span>
                                </div>
                            </RadioButton>
                            <RadioButton
                                id={"afterXTime"}
                                ariaLabelledBy={"afterXTime-rblabel"}
                                key={"afterXTime"}
                                className="policy-build-radio-button margin-top-16"
                                disabled={this.disabled}
                            >
                                <div className="bolt-radio-button-label" id={getSafeId("afterXTime-rblabel")}>
                                    <span className="build-policy-inline">
                                        <span>{Resources.BuildExpirationAfterTime1}</span>
                                        <div className="build-policy-inline-block">
                                            <FormItem>
                                                <Observer selectedExpirationType={this.selectedExpirationType}>
                                                    {() => {
                                                        return (
                                                            <TextField
                                                                ariaLabel={Resources.BuildExpiration}
                                                                value={this.buildExpirationValue}
                                                                onChange={(event, value) => {
                                                                    this.buildExpirationValue.value = value;
                                                                    this.policy.complexSettings!.validDuration = value;

                                                                    this.errorMessageAfterXHours.value = undefined;

                                                                    if (this.selectedExpirationType.value === "afterXTime") {
                                                                        this.errorMessageAfterXHours.value =
                                                                            this.buildExpirationValue.value.length > 0 &&
                                                                            !isNaN(Number(this.buildExpirationValue.value))
                                                                                ? undefined
                                                                                : Resources.ValidationNumberDecimal;
                                                                    }
                                                                }}
                                                                disabled={this.disabled || this.selectedExpirationType.value != "afterXTime"}
                                                                className="build-policies-afterXTime-width"
                                                            />
                                                        );
                                                    }}
                                                </Observer>
                                            </FormItem>
                                        </div>
                                        {Resources.BuildExpirationAfterTime2}
                                        <span>
                                            <Icon ariaHidden={"true"} className={"build-policies-afterXTime-icon"} iconName={"OpenSource"} />
                                            <span>{this.policy.confgiuredOn || this.policy.appliesTo}</span>
                                        </span>
                                        <span>{Resources.BuildExpirationAfterTime3}</span>
                                    </span>
                                </div>
                            </RadioButton>
                            <RadioButton
                                id={"never"}
                                ariaLabelledBy={"never-rblabel"}
                                key={"never"}
                                className="policy-build-radio-button margin-top-16"
                                disabled={this.disabled}
                            >
                                <div className="bolt-radio-button-label" id={getSafeId("never-rblabel")}>
                                    {Resources.BuildExpirationNever}
                                </div>
                            </RadioButton>
                        </RadioButtonGroup>
                    </FormItem>
                </div>
                <div>
                    <FormItem label={Resources.DisplayName}>
                        <TextField
                            value={this.displayNameValue}
                            onChange={(event, value) => {
                                this.displayNameValue.value = value;
                                this.policy.complexSettings!.displayName = value;
                            }}
                            width={TextFieldWidth.auto}
                            disabled={this.disabled}
                            className="flex-grow"
                            containerClassName="flex-grow"
                            inputClassName="flex-grow"
                        />
                    </FormItem>
                </div>
            </div>
        );

        return (
            <Observer
                isBusy={this.isBusy}
                errorMessageAfterXHours={this.errorMessageAfterXHours}
                noBuildDefsMessage={this.showNoBuildDefsMessage}
                policy={this.props.policy}
            >
                {() => {
                    return (
                        <Panel
                            onDismiss={() => this.props.onDismiss()}
                            titleProps={{ text: this.title }}
                            footerButtonProps={[
                                {
                                    text: Resources.Save,
                                    primary: true,
                                    onClick: () => {
                                        this.isBusy.value = true;
                                        this.props.save(this.policy, this.handleError);
                                    },
                                    disabled: this.disabled || !!this.errorMessageAfterXHours.value || this.showNoBuildDefsMessage.value
                                },
                                {
                                    text: Resources.Cancel,
                                    onClick: () => this.props.onDismiss()
                                }
                            ]}
                            description={
                                this.policy.isEnterpriseManaged
                                    ? Resources.ReadOnlyTooltip
                                    : this.policy.isInherited
                                    ? Resources.InheritanceMessageTable
                                    : undefined
                            }
                            overlayContent={this.isBusy.value ? <Spinner /> : undefined}
                        >
                            {!this.showNoBuildDefsMessage.value && content}
                            {this.showNoBuildDefsMessage.value && <div className="secondary-text">{Resources.NoBuildDefsMessage}</div>}
                        </Panel>
                    );
                }}
            </Observer>
        );
    };

    private handleError = (error: any) => {
        this.isBusy.value = false;
        this.failureMessage.value = error.toString();
    };

    private selectInitialDropdownValue = () => {
        const { buildDefinitions } = this.props;
        if (!buildDefinitions.value || !buildDefinitions.length || isNaN(Number(buildDefinitions.value[0].id))) {
            this.showNoBuildDefsMessage.value = true;
        } else {
            this.showNoBuildDefsMessage.value = false;
            let index = this.getInitialDropdownIndex();

            if (index < 0) { 
                return;
            }

            if (buildDefinitions.value[index].id !== this.policy.complexSettings!.buildDefinitionId.toString()) {
                this.policy.complexSettings!.buildDefinitionId = Number(buildDefinitions.value[index].id) | 0;
            }

            this.selection.select(index);
        }
    };


    private getInitialDropdownIndex(): number { 
        const { buildDefinitions } = this.props;
        let index = 0;
        if (this.policy.complexSettings!.buildDefinitionId !== -1) {
            index = buildDefinitions.value.findIndex((value: IListBoxItem) => {
                return value.id === this.policy.complexSettings!.buildDefinitionId.toString();
            });
        }
        return index;
    }
}
